import { useEffect, useState } from 'react';
import { CasePermissionsType, FormInputVariant, FormState } from 'src/types';
import { InviteUserDropdownOption } from '../../DropdownSelect';
import { FormInput } from '../../FormInput';
import { SearchFieldDescription } from '../InviteToCaseModal.styles';
import { FadeOutHeading, SlideUp } from './AccessStep.styles';

import {
  StyledIcon,
  StyledSubheader,
} from 'src/components/foundations/foundations.styles';

import { useSelector } from 'react-redux';
import { OnChangeValue } from 'react-select';
import { inviteToCaseModal } from 'src/components/pages/Case/Case.slice';
import { useTheme } from 'styled-components';
import { InformationBanner } from '../..';
import { Access } from '../components/Access/Access';
import { GlobalAccess } from '../components/GlobalAccess/GlobalAccess';
import { accessStepPermissionHelper } from '../helpers/accessStepPermissionHelper';
import {
  AccessStepDescription,
  AccessStepIntro,
  GlobalAccessContainer,
  StyledSubheaderContainer,
} from './AccessStep.styles';

interface AccessStepProps {
  selectedTeamMembers: (InviteUserDropdownOption & {
    isSuperAdmin?: boolean;
  })[];
  permissions: CasePermissionsType[];
  handleSetPermissions: (
    permissions: CasePermissionsType[],
    action: 'add' | 'remove',
  ) => void;
  handleSelectTeamMembers: (
    optionsSelected: OnChangeValue<InviteUserDropdownOption, boolean>,
  ) => void;
}

export const AccessStep = ({
  selectedTeamMembers,
  permissions,
  handleSetPermissions,
  handleSelectTeamMembers,
}: AccessStepProps) => {
  const theme = useTheme();
  const { readableCaseId, unableToChat } = useSelector(inviteToCaseModal);

  const [fadeOutHeading, setFadeOutHeading] = useState(true);
  const animationPadding = 100;
  const fadeOutAnimationDuration = 300;
  const slideUpAnimationDuration = 300;

  useEffect(() => {
    setTimeout(() => {
      setFadeOutHeading(false);
    }, fadeOutAnimationDuration - animationPadding);
  }, []);

  const {
    hasAllViewPermissions,
    hasAllEditPermissions,
    disabledViewReportPII,
    disabledViewCasePII,
    ALL_VIEW_PERMISSIONS,
    ALL_EDIT_PERMISSIONS,
  } = accessStepPermissionHelper(permissions, unableToChat);

  const handleClickAllEditAccess = () => {
    handleSetPermissions(ALL_VIEW_PERMISSIONS, 'remove');
    handleSetPermissions(ALL_EDIT_PERMISSIONS, 'add');
  };

  const handleClickAllViewAccess = () => {
    handleSetPermissions(ALL_EDIT_PERMISSIONS, 'remove');
    handleSetPermissions(ALL_VIEW_PERMISSIONS, 'add');
  };

  const showSuperAdminWarning = Boolean(
    selectedTeamMembers.find((manager) => manager.isSuperAdmin === true),
  );

  return (
    <>
      {fadeOutHeading && (
        <FadeOutHeading animationDuration={fadeOutAnimationDuration}>
          <StyledSubheader>Invite people to your case</StyledSubheader>
          <SearchFieldDescription>
            Expand your team by inviting individuals to collaborate on your case
          </SearchFieldDescription>
        </FadeOutHeading>
      )}
      <SlideUp
        animationDelay={fadeOutAnimationDuration}
        animationDuration={slideUpAnimationDuration}
        fadeOutComplete={!fadeOutHeading}
        marginBottom={showSuperAdminWarning ? 30 : 0}
      >
        <FormInput<InviteUserDropdownOption>
          variant={FormInputVariant.DROPDOWN}
          placeholder="Search by name"
          state={FormState.DEFAULT}
          onChange={handleSelectTeamMembers}
          options={[]}
          value={selectedTeamMembers.map((manager) => ({
            ...manager,
            tagStateWhenSelected: manager.isSuperAdmin
              ? FormState.WARNING
              : undefined,
          }))}
          isMulti
          hideDropdownIndicator={true}
          hideNoOptionsMessage={true}
        />
      </SlideUp>

      {showSuperAdminWarning && (
        <InformationBanner
          data-testid="super-admin-warning"
          state={FormState.WARNING}
          title="These users have full access automatically"
          description="This means they have unrestricted access to your case. Please bear this in mind when adding members to your team"
        />
      )}

      <AccessStepIntro>
        <StyledSubheaderContainer>
          <StyledIcon
            size="24px"
            color={theme.atlas.semantic.color.type.default}
            className="uil uil-padlock"
          />
          <StyledSubheader>{`Total access to Case ID ${readableCaseId}`}</StyledSubheader>
        </StyledSubheaderContainer>
        <AccessStepDescription>
          Choose the appropriate access level for the entire case.
        </AccessStepDescription>
      </AccessStepIntro>

      <GlobalAccessContainer>
        <GlobalAccess
          globalAccess={hasAllEditPermissions}
          onClick={handleClickAllEditAccess}
          data-testid={'all-edit-access'}
        >
          <StyledSubheader>{'Editor access'}</StyledSubheader>
          <AccessStepDescription>
            Grant full control and unrestricted editing for the entire case,
            allowing modification of all relevant information.
          </AccessStepDescription>
        </GlobalAccess>

        <GlobalAccess
          globalAccess={hasAllViewPermissions}
          onClick={handleClickAllViewAccess}
          data-testid={'all-read-access'}
        >
          <StyledSubheader>{'Read-only access'}</StyledSubheader>
          <AccessStepDescription>
            Grant read-only access for the entire case, allowing viewing of all
            relevant information.
          </AccessStepDescription>
        </GlobalAccess>
      </GlobalAccessContainer>
      <Access
        permissions={permissions}
        handleSetPermissions={handleSetPermissions}
        disabledViewCasePII={disabledViewCasePII}
        disabledViewReportPII={disabledViewReportPII}
        unableToChat={unableToChat}
        disablePermissions={false}
      />
    </>
  );
};
